import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { fetchRsvpDetails } from '../BackendRoute';
import { useLanguage } from '../LanguageContext';
import { useSnackbar } from '../SnackbarContext';
import Pic1 from '../assets/images/pages/1_DSC00770.webp';
import ReviewStep from '../rsvp-form/ReviewStep';
import { getTokenFromCookie } from '../utils/cookieUtils';

function RSVPResponseModal({ setIsModalOpen, rsvpDetails }) {
  const { t } = useLanguage();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return createPortal(
    <div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={() => setIsModalOpen(false)}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="w-full max-w-md bg-white/95 rounded-3xl border border-white/40 shadow-2xl p-6 md:p-8 overflow-y-auto max-h-[85vh] relative no-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-4 right-4 text-nav-comp-text hover:text-rose-accent transition-colors duration-300 w-8 h-8 rounded-full flex items-center justify-center bg-wedding-blush/20 hover:bg-wedding-blush/50 font-sans"
            aria-label="Close"
          >
            ✕
          </button>

          {/* ReviewStep Content */}
          <ReviewStep
            titleKey="rsvp_form.view_review"
            formData={rsvpDetails?.responses || {}}
            allGuests={rsvpDetails?.all_guests || []}
          />

          {/* Action Button to close */}
          <div className="mt-8">
            <button
              onClick={() => setIsModalOpen(false)}
              className="w-full py-3 px-4 bg-rose-accent hover:bg-rose-hover text-white font-raleway font-semibold text-xs uppercase tracking-[0.2em] rounded-full shadow-sm transition-all duration-300"
            >
              {t('rsvp_form.back') || 'Close'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>,
    document.body,
  );
}

export default function RsvpPage() {
  const navigate = useNavigate();
  const [hasRsvp, setHasRsvp] = useState(false);
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { t } = useLanguage();
  const imgRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState('100dvh');

  const updateContainerHeight = () => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const ratio = img.naturalHeight / img.naturalWidth;
    const renderedHeight = window.innerWidth * ratio;
    setContainerHeight(`${Math.max(renderedHeight, window.innerHeight)}px`);
  };

  useEffect(() => {
    window.addEventListener('resize', updateContainerHeight);
    return () => window.removeEventListener('resize', updateContainerHeight);
  }, []);

  const getHasRsvp = async () => {
    // Try to use token from cookie, fallback to id from URL
    const token = getTokenFromCookie();
    if (!token) {
      showSnackbar(t('main.error_no_token'), 'error');
      return;
    }
    const details = await fetchRsvpDetails(token, showSnackbar);
    if (details) {
      setRsvpDetails(details);
      const isRsvpResponsePresent = Object.keys(details.responses).length > 0;
      setHasRsvp(isRsvpResponsePresent);
    }
  };

  useEffect(() => {
    getHasRsvp();
  }, []);

  return (
    <div className="w-full flex-grow flex flex-col">
      {/* Image container — height = max(image natural height at current width, screen height) */}
      <div className="relative w-full" style={{ height: containerHeight }}>
        <img
          ref={imgRef}
          src={Pic1}
          alt="Daniel & Tracer"
          onLoad={updateContainerHeight}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Gradient: same bounds as the image */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/45 pointer-events-none" />

        {/* Card: pinned to the bottom portion of the image */}
        <div className="absolute bottom-0 left-0 right-0 pb-6 px-4 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full bg-white/75 backdrop-blur-[2px] rounded-3xl border border-white/50 shadow-lg p-4 text-center"
          >
            {/* Date Information */}
            <div className="mb-2">
              <p className="text-5xl tracking-[0.2em] text-panel-title tracking-wide">
                Daniel & Tracer
              </p>
              <p className="text-xl text-panel-subtitle">
                {t('main.full_date')}
              </p>
            </div>

            {/* Action Button */}
            <div className="flex flex-col gap-3">
              <motion.button
                onClick={() => navigate('/rsvp')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-wedding-primary transition-all duration-300"
              >
                {hasRsvp ? t('main.edit_rsvp_btn') : t('main.rsvp_btn')}
              </motion.button>

              {hasRsvp && (
                <motion.button
                  onClick={() => setIsModalOpen(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-wedding-inverted transition-all duration-300"
                >
                  {t('main.view_rsvp_btn')}
                </motion.button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <RSVPResponseModal
            setIsModalOpen={setIsModalOpen}
            rsvpDetails={rsvpDetails}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
